import { AddressAccess, TransportCompany } from 'contexts/account/types/account-company.type';
import dayjs from 'dayjs';
import _ from 'underscore';
import { CustomerAssortmentProduct, CustomerAssortment } from 'views/order-feed/types/customer-assortment.type';
import { DispatchSchedule } from 'views/order-feed/types/shipping-schedule.type';

type OrderDateParts = {
  requestedDispatchDate: string;
  plantId?: string;
  vessel?: string;
};

const VESSEL_TRANSPORT_METHOD = '13';

const formatDate = (date: string) => {
  return dayjs(date).format('YYYY-MM-DD');
};

const isDateWithinProductRange = (
  products: CustomerAssortmentProduct[] | undefined,
  itemNumber: string | null | undefined,
  date: string
): boolean => {
  if (!itemNumber) return true;

  const article = _.chain(products)
    .find((e) => e.itemNumber === itemNumber)
    .value();

  if (!article) return false;
  return article.validityRanges.some((r) => dayjs(date).isBetween(r.validFrom, r.validTo, 'days', '[]'));
};

const isItemVisibleInProductList = (
  showPreferredProducts: boolean,
  products: CustomerAssortment | undefined,
  itemNumber: string,
  searchValue: string
) =>
  (showPreferredProducts &&
    products?.preferredProducts.some((prefProd) => isProductInSearchValue(prefProd, itemNumber, searchValue))) ||
  (!showPreferredProducts &&
    products?.availableProducts.some((availProd) => isProductInSearchValue(availProd, itemNumber, searchValue)));

const isProductInSearchValue = (product: CustomerAssortmentProduct, itemNumber: string, searchValue: string) =>
  product.itemNumber === itemNumber &&
  (product.itemName.toLowerCase().includes(searchValue.toLowerCase()) ||
    product.itemNumber.includes(searchValue));

const getOrderDateValue = (schedule: DispatchSchedule['dispatchDateData'][number]): string =>
  getOrderDateValueFromParts(schedule.factoryId, schedule.vessel, schedule.dispatchDate);

const getOrderDateParts = (value: string): OrderDateParts => {
  const parts = value.split('_');

  if (parts.length < 3) {
    return { requestedDispatchDate: value };
  }

  const plantId = parts[0];
  const requestedDispatchDate = parts[parts.length - 1];
  const vessel = parts.slice(1, parts.length - 1).join('_');

  return { plantId, vessel, requestedDispatchDate };
};

const getOrderDateValueFromParts = (plantId: string, vessel: string, dispatchDate: string): string => {
  if (!dispatchDate) return '';

  return plantId && vessel ? `${plantId}_${vessel}_${formatDate(dispatchDate)}` : formatDate(dispatchDate);
};

const getDefaultDispatchDate = (shippingSchedule: DispatchSchedule | undefined, minimumDispatchDate?: string): string => {
  const defaultDate = shippingSchedule?.earliestPossibleDispatchDate || dayjs().add(10, 'days').toISOString();
  const schedule = shippingSchedule?.dispatchDateData.find(
    (item) =>
      dayjs(item.dispatchDate).isSameOrAfter(defaultDate) &&
      (!minimumDispatchDate || dayjs(item.dispatchDate).isAfter(minimumDispatchDate))
  );

  if (schedule) return getOrderDateValue(schedule);

  return minimumDispatchDate && dayjs(minimumDispatchDate).isAfter(defaultDate) ? minimumDispatchDate : defaultDate;
};

const isOrderDateInShippingSchedule = (shippingSchedule: DispatchSchedule | undefined, dateValue: string): boolean => {
  if (!shippingSchedule?.dispatchDateData.length) return true;

  const { plantId, vessel, requestedDispatchDate } = getOrderDateParts(dateValue);

  if (!(plantId && vessel)) return false;

  return shippingSchedule.dispatchDateData.some(
    (schedule) =>
      schedule.factoryId === plantId &&
      schedule.vessel === vessel &&
      formatDate(schedule.dispatchDate) === formatDate(requestedDispatchDate)
  );
};

const getSelectedTransportCompany = (
  transportCompanies: TransportCompany[] | undefined,
  transportMethod: string
): TransportCompany | undefined => {
  return (
    transportCompanies?.find(
      (transportCompany) => transportCompany.key === transportMethod || transportCompany.name === transportMethod
    ) ?? transportCompanies?.[0]
  );
};

const getSiteTransportCompanies = (
  addressAccess: AddressAccess[] | undefined,
  globalSiteId: string
): TransportCompany[] | undefined => {
  return addressAccess?.find((address) => address.id === globalSiteId)?.transportCompanies;
};

const shouldSendPlantAndVessel = (transportMethod: string, plantId: string | undefined, vessel: string | undefined): boolean => {
  return transportMethod === VESSEL_TRANSPORT_METHOD && !!plantId && !!vessel;
};

export {
  formatDate,
  getDefaultDispatchDate,
  getOrderDateParts,
  getOrderDateValue,
  getOrderDateValueFromParts,
  getSelectedTransportCompany,
  getSiteTransportCompanies,
  isDateWithinProductRange,
  isItemVisibleInProductList,
  isOrderDateInShippingSchedule,
  shouldSendPlantAndVessel,
};
